/**
 * Where the till keeps the staff verifiers it has already minted.
 *
 * `ShiftSession.installCredentials` existed from the day enrolment was written
 * and HAD NO CALLER, so the bundle lived in memory only. Every page reload — a
 * tab refresh, a tablet waking up, a crash mid-service — threw it away, and
 * the next sign-in needed the network again. On a till whose entire premise is
 * that a PIN is verified locally, that is the one thing it must not need.
 *
 * SAME `SqliteDriver`, SAME OPFS DATABASE as the outbox, the drawer journal and
 * the lease store: one database to open, one set of durability settings, and no
 * second thing that can be half-migrated.
 *
 * WHAT IS STORED IS NOT A SECRET, and that is what makes this safe. A verifier
 * is Argon2id over an HMAC-peppered PIN, salted with a DEVICE SECRET that never
 * appears in this table. Lifted off the disk and carried to another tablet it
 * verifies nothing, because that tablet's secret is different. The pepper is
 * the server's and is not here either.
 *
 * ONE BUNDLE, keyed on nothing — a device belongs to one venue and holds one
 * shift's credentials at a time. A second row would be a second venue's staff
 * able to sign in on this till, which is not a feature.
 */

import type { SqliteDriver } from "@gf/kernel/sqlite";
import type { ShiftCredentialBundle } from "@gf/kernel/staff-auth";

interface Row {
  readonly bundle_json: string;
  readonly expires_at: number;
}

export class SqliteCredentialStore {
  readonly #driver: SqliteDriver;

  constructor(driver: SqliteDriver) {
    this.#driver = driver;
  }

  /** Idempotent: every app start runs it. */
  async migrate(): Promise<void> {
    await this.#driver.exec(`
      CREATE TABLE IF NOT EXISTS staff_credential (
        id          INTEGER NOT NULL PRIMARY KEY CHECK (id = 1),
        bundle_json TEXT    NOT NULL,
        expires_at  INTEGER NOT NULL
      )
    `);
  }

  /**
   * Replace whatever is held.
   *
   * REPLACE rather than merge. A fresh enrolment answers with the whole bundle
   * for this device, and merging would keep a credential the server has since
   * revoked — somebody who left, still able to open a drawer.
   */
  async put(bundle: ShiftCredentialBundle): Promise<void> {
    await this.#driver.run(
      `INSERT INTO staff_credential (id, bundle_json, expires_at)
       VALUES (1, ?, ?)
       ON CONFLICT (id) DO UPDATE SET bundle_json = excluded.bundle_json,
                                      expires_at  = excluded.expires_at`,
      [JSON.stringify(bundle), Date.parse(bundle.expiresAt)],
    );
  }

  /**
   * What is held, or null — including when it has EXPIRED.
   *
   * Expiry is enforced on read rather than by a sweep, because a till that is
   * off for a fortnight never runs a sweep. Thirty days past its shift is long
   * enough for a server to sign in offline all month and short enough that
   * somebody who left stops being able to.
   */
  async get(now: Date): Promise<ShiftCredentialBundle | null> {
    const rows = await this.#driver.all<Row>(
      `SELECT bundle_json, expires_at FROM staff_credential WHERE id = 1`,
      [],
    );
    const row = rows[0];
    if (!row) return null;
    if (row.expires_at <= now.getTime()) {
      await this.clear();
      return null;
    }

    try {
      return JSON.parse(row.bundle_json) as ShiftCredentialBundle;
    } catch {
      // A row we cannot parse is a row from a version that no longer exists.
      // Dropping it costs one online enrolment; keeping it costs every one.
      await this.clear();
      return null;
    }
  }

  async clear(): Promise<void> {
    await this.#driver.run(`DELETE FROM staff_credential WHERE id = 1`, []);
  }
}
